import { useState } from "react";
import Logo from "images/comporium-logo.png";
import { useLocation, useNavigate } from "react-router-dom";
import { Badge, Navbar, Col, Row, Modal, Container } from "react-bootstrap";
import { postClearOrder } from "services/post-clearOrder";
import { postRemoveFromCart } from "services/post-removeFromCart";
import { useSetRecoilState, useRecoilValue } from "recoil";
import { overlayState, cartState, isExistingCustomerState } from "atoms";
import CartIcon from "components/CartIcon";
import useResetMultipleStates from "hooks/reset-state";
import { useWrapRequest } from "utils/index";
import { Link } from "react-router-dom";
import { ResponseObject, Self } from "atoms/models";
import BackToPlansMobileButton from "./Customize/BackToPlansMobileButton";
import { PATH } from "utils/const";

interface Links {
  self: Self;
  "ec:windowLocation"?: { href: string };
}

const STEPS = [
  { path: PATH.CUSTOMIZE, name: "Customize" },
  { path: PATH.CHECKOUT, name: "Checkout" },
  { path: PATH.SCHEDULE, name: "Schedule" },
  { path: PATH.CONFIRM, name: "Confirm" },
  { path: PATH.PAYMENT, name: "Payment" },
];

function MainNav() {
  const [showChangeAddressModal, setShowChangeAddressModal] = useState(false);
  const [showEmptyCartModal, setShowEmptyCartModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const wrapRequest = useWrapRequest();
  const resetState = useResetMultipleStates();
  const setShowOverlay = useSetRecoilState(overlayState);
  const cart = useRecoilValue(cartState);
  const isExistingCustomer = useRecoilValue(isExistingCustomerState);
  const userAddress =
    cart && Object.keys(cart).length > 0 && location.pathname !== PATH.ADDRESS_SEARCH
      ? `${cart?.serviceStreetNumber} ${cart?.serviceStreetName} ${cart?.serviceApartmentNumber}, ${cart?.serviceCity}, ${cart?.serviceState} ${cart?.serviceZipCode}`
      : null;

  const dontShowAddressInformationAndButton =
    location.pathname === PATH.ROOT ||
    location.pathname === PATH.CUSTOMER_INFO_K ||
    location.pathname === PATH.SERVICE_REQUEST ||
    location.pathname === PATH.SERVICE_REQUEST_CONF ||
    location.pathname === PATH.ADDRESS_SEARCH ||
    location.pathname === PATH.THANK_YOU_K ||
    location.pathname === PATH.THANK_YOU ||
    location.pathname === PATH.RESTORE_CART_ERROR ||
    location.pathname === PATH.MARKETING ||
    location.pathname === PATH.NOT_FOUND;

  const dontShowBreadCrumb =
    location.pathname === PATH.ROOT ||
    location.pathname === PATH.CUSTOMER_INFO_K ||
    location.pathname === PATH.SERVICE_REQUEST ||
    location.pathname === PATH.SERVICE_REQUEST_CONF ||
    location.pathname === PATH.ADDRESS_SEARCH ||
    location.pathname === PATH.BROWSE ||
    location.pathname === PATH.THANK_YOU_K ||
    location.pathname === PATH.THANK_YOU ||
    location.pathname === PATH.CUSTOMER_SUMMARY ||
    location.pathname === PATH.E_KICK ||
    location.pathname === PATH.RESTORE_CART_ERROR ||
    location.pathname === PATH.MARKETING ||
    location.pathname === PATH.CANCEL ||
    location.pathname === PATH.SORRY ||
    location.pathname === PATH.NOT_FOUND;

  const dontShowCartIcon =
    location.pathname === PATH.ROOT ||
    location.pathname === PATH.CUSTOMER_INFO_K ||
    location.pathname === PATH.SERVICE_REQUEST ||
    location.pathname === PATH.SERVICE_REQUEST_CONF ||
    location.pathname === PATH.ADDRESS_SEARCH ||
    location.pathname === PATH.THANK_YOU_K ||
    location.pathname === PATH.CONFIRM ||
    location.pathname === PATH.THANK_YOU ||
    location.pathname === PATH.E_KICK;

  const showReturnToAccountSummary =
    cart?.isCurrentCustomer &&
    (location.pathname === PATH.CONFIRM ||
      location.pathname === PATH.BROWSE ||
      location.pathname === PATH.CUSTOMIZE ||
      location.pathname === PATH.SCHEDULE ||
      location.pathname === PATH.PAYMENT ||
      location.pathname === PATH.CHECKOUT);

  const setReturnModalText = () => {
    if ((cart?.isCurrentCustomer && location.pathname === PATH.THANK_YOU) || location.pathname === PATH.E_KICK) {
      return <div className="text-center">Would you like to navigate back to your account?</div>;
    } else if (cart?.isCurrentCustomer) {
      return <div className="text-center">Are you sure you want to leave this page? All your shopping changes will be lost.</div>;
    } else {
      return (
        <div className="text-center">
          Changing the Service Address now will remove everything from your cart and you will need to start over. Do you want to change the service
          address?
        </div>
      );
    }
  };

  const activeStepIndex = STEPS.findIndex((item: any) => location.pathname === item.path);

  const handleChangeAddress = async () => {
    setShowOverlay(true);
    setShowChangeAddressModal(false);
    const cancelReason = isExistingCustomer ? "Go to my Comporium button clicked." : "Not my address button clicked.";
    const cartCreated = isExistingCustomer ? false : true;
    const response: ResponseObject = await postClearOrder(cancelReason, cartCreated);
    if (response.message === "Bad Request") {
      navigate("address-search");
    } else {
      const { _links: links = {} as Links } = response;
      const windowLocationHref = links["ec:windowLocation"]?.href;
      if (windowLocationHref) {
        if (windowLocationHref.includes("shop")) {
          navigate(windowLocationHref);
        } else {
          window.location.href = windowLocationHref;
        }
      }
    }
  };

  const onEmptyCart = () => {
    wrapRequest(async () => {
      const removeResponse = await postRemoveFromCart(0, false, false);
      if (removeResponse?.ecommerceAction === "continue") {
        setShowEmptyCartModal(false);
        resetState();
        navigate(PATH.BROWSE);
      } else if (removeResponse.ecommerceResponse?.ecommerceAction === "error") {
        throw removeResponse.ecommerceResponse?.message;
      }
    });
  };

  return (
    <div className="navbar-container">
      <div className="actions-top-bar">
        <div className="container">
          {dontShowCartIcon ? null : <CartIcon />}
          <div className="sliding-top-control">
            <div className="header-mark"></div>
          </div>
        </div>
      </div>
      <Navbar className="bg-body-tertiary">
        <Container>
          <Navbar.Brand>
            <img src={Logo} alt="comporium" className="logo" onClick={() => (window.location.href = "https://www.comporium.com")} />
          </Navbar.Brand>
          <Navbar.Toggle />
          <Navbar.Collapse className="justify-content-end">
            <Navbar.Text>
              <div className="address-line">{userAddress}</div>
              {dontShowAddressInformationAndButton ? null : (
                <>
                  <div>
                    <button
                      className="btn btn-light"
                      onClick={() => {
                        location.pathname === PATH.SORRY ? handleChangeAddress() : setShowChangeAddressModal(true);
                      }}
                    >
                      {cart?.isCurrentCustomer || location.pathname === PATH.E_KICK ? "Return to My Comporium" : "Change Address"}
                    </button>
                  </div>
                  {showReturnToAccountSummary ? (
                    <Link to={PATH.CUSTOMER_SUMMARY} className="btn btn-primary btn-sm customer-summary-link">
                      Return to Account Summary
                    </Link>
                  ) : null}
                  {cart?.list?.cartProducts?.length > 0 && !cart.isCurrentCustomer ? (
                    <div>
                      <button className="btn btn-light" onClick={() => setShowEmptyCartModal(true)}>
                        Empty Cart and Start Over
                      </button>
                    </div>
                  ) : null}
                </>
              )}
              {location.pathname === PATH.THANK_YOU && cart.isCurrentCustomer ? (
                <div>
                  <button
                    className="btn btn-light"
                    onClick={() => {
                      setShowChangeAddressModal(true);
                    }}
                  >
                    Return to My Comporium
                  </button>
                </div>
              ) : null}
            </Navbar.Text>
          </Navbar.Collapse>
        </Container>
      </Navbar>
      {dontShowBreadCrumb ? null : (
        <div className="breadcrumbs">
          <Container fluid={true} className="bg-black">
            <Row>
              <Col>
                <Container>
                  <Row>
                    <Col className="py-3 fw-bold d-flex">
                      {STEPS.map((step: any, index: number) => {
                        const isLast = index === STEPS.length - 1;

                        let badgeClass = "step-inactive opacity-75 p-2";
                        let nameClass = "text-white step-inactive-text opacity-75 me-2";
                        let dividerClass = "step-divider inactive me-2";

                        const isActive = activeStepIndex === index;
                        const isCompleted = activeStepIndex !== -1 && activeStepIndex > index;

                        if (isActive) {
                          badgeClass = "bg-gold p-2";
                          nameClass = "text-white me-2";
                          dividerClass = "step-divider step-divider-active me-2";
                        } else if (isCompleted) {
                          badgeClass = "step-complete opacity-75 p-2";
                          nameClass = "text-white step-complete-text opacity-75 me-2";
                          dividerClass = "step-divider complete me-2";
                        }

                        const role = isCompleted ? "button" : undefined;
                        const clickHandler = isCompleted ? () => navigate(step.path) : undefined;

                        return (
                          <div key={step.path} className="step-path">
                            <Badge role={role} onClick={clickHandler} pill className={badgeClass} style={{ width: "28px" }} bg="">
                              {index + 1}
                            </Badge>
                            <span role={role} onClick={clickHandler} className={nameClass}>
                              {step.name}
                            </span>
                            {!isLast && <span className={dividerClass}></span>}
                          </div>
                        );
                      })}
                    </Col>
                  </Row>
                </Container>
              </Col>
            </Row>
          </Container>
          {location.pathname === PATH.CUSTOMIZE ? <BackToPlansMobileButton /> : null}
        </div>
      )}

      <Modal show={showEmptyCartModal} onHide={() => setShowEmptyCartModal(false)} size="lg" className="change-address-modal">
        <Modal.Body>
          <div className="p-5">
            <p className="mb-5">
              Emptying the cart will remove all products from the cart and return you to the Browse page to start over. Do you want to empty the cart?
            </p>
            <div className="d-grid gap-2 d-flex justify-content-center">
              <button className="btn btn-primary live-here continue mx-2" onClick={onEmptyCart}>
                Continue
              </button>
              <button className="btn btn-primary move-here cancel mx-2" onClick={() => setShowEmptyCartModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showChangeAddressModal} onHide={() => setShowChangeAddressModal(false)} size="lg" className="change-address-modal">
        <Modal.Body>
          <div className="text-end">
            <i className="fa-solid fa-xmark ms-3 mt-1" onClick={() => setShowChangeAddressModal(false)}></i>
          </div>
          <div className="p-4">
            <p className="mb-5">{setReturnModalText()}</p>
            <div className="d-grid gap-2 d-flex justify-content-center">
              <button
                className="btn btn-primary live-here continue me-4"
                onClick={() => {
                  handleChangeAddress();
                  resetState();
                  if (cart?.isCurrentCustomer) {
                    localStorage.removeItem("msAuthToken");
                  }
                }}
              >
                {cart?.isCurrentCustomer ? "Leave Page" : "Continue"}
              </button>
              <button className="btn btn-primary move-here cancel" onClick={() => setShowChangeAddressModal(false)}>
                {cart?.isCurrentCustomer ? "Stay On This Page" : "Cancel"}
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default MainNav;
